import {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
  type ReactNode,
} from 'react';
import type { EnrichedCmailMessage } from '../../../api/cmail/cmail.dto';

interface ExtractionPanelContextValue {
  isOpen: boolean;
  msgId: string | null;
  message: EnrichedCmailMessage | null;
  openPanel: (msgId: string, message: EnrichedCmailMessage) => void;
  closePanel: () => void;
  togglePanel: (msgId: string, message: EnrichedCmailMessage) => void;
}

const ExtractionPanelContext =
  createContext<ExtractionPanelContextValue | null>(null);

interface ExtractionPanelProviderProps {
  children: ReactNode;
}

export const ExtractionPanelProvider = ({
  children,
}: ExtractionPanelProviderProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [msgId, setMsgId] = useState<string | null>(null);
  const [message, setMessage] = useState<EnrichedCmailMessage | null>(null);

  const openPanel = useCallback(
    (newMsgId: string, newMessage: EnrichedCmailMessage) => {
      setMsgId(newMsgId);
      setMessage(newMessage);
      setIsOpen(true);
    },
    []
  );

  const closePanel = useCallback(() => {
    setIsOpen(false);
  }, []);

  const togglePanel = useCallback(
    (newMsgId: string, newMessage: EnrichedCmailMessage) => {
      if (isOpen && msgId === newMsgId) {
        setIsOpen(false);
      } else {
        setMsgId(newMsgId);
        setMessage(newMessage);
        setIsOpen(true);
      }
    },
    [isOpen, msgId]
  );

  const value = useMemo(
    () => ({
      isOpen,
      msgId,
      message,
      openPanel,
      closePanel,
      togglePanel,
    }),
    [isOpen, msgId, message, openPanel, closePanel, togglePanel]
  );

  return (
    <ExtractionPanelContext.Provider value={value}>
      {children}
    </ExtractionPanelContext.Provider>
  );
};

export const useExtractionPanel = () => {
  const context = useContext(ExtractionPanelContext);
  if (!context) {
    throw new Error(
      'useExtractionPanel must be used within ExtractionPanelProvider'
    );
  }
  return context;
};

export const useExtractionPanelOptional = () => {
  return useContext(ExtractionPanelContext);
};
